/**
 * Endpoints de **Finanzas → Ingresos** y **Egresos administrativos**
 * (`/api/finanzas/ingresos` y `/api/finanzas/egresos`).
 *
 * Las dos pantallas comparten todo menos el tipo, así que los hooks y las
 * mutaciones se crean con una fábrica que recibe `tipo`: el listado va paginado
 * en el servidor con sus catálogos, y el alta, la edición y la anulación son
 * `multipart` (el comprobante viaja en el mismo formulario).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoAdministrativo,
  DetalleAdministrativo,
  FiltrosAdministrativos,
  ListadoAdministrativos,
  TipoAdministrativo,
} from './administrativos.types';

export const administrativosKeys = {
  todo: (tipo: TipoAdministrativo) => ['finanzas', tipo] as const,
  lista: (tipo: TipoAdministrativo, filtros: FiltrosAdministrativos) =>
    ['finanzas', tipo, 'lista', filtros] as const,
  detalle: (tipo: TipoAdministrativo, id: number) => ['finanzas', tipo, 'detalle', id] as const,
};

/** Cadena de consulta del listado y de las exportaciones. */
export function cadenaDeAdministrativos(filtros: FiltrosAdministrativos): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.subtipo) parametros.set('subtipo', filtros.subtipo);
  if (filtros.cuenta) parametros.set('cuenta', filtros.cuenta);
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

/** Formulario del alta y de la edición (el comprobante es opcional según el tipo). */
function formularioDe(datos: CuerpoAdministrativo): FormData {
  const formulario = new FormData();

  formulario.set('subtipo_id', datos.subtipo_id);
  formulario.set('cuenta_id', datos.cuenta_id);
  formulario.set('metodo_pago_id', datos.metodo_pago_id);
  formulario.set('monto', datos.monto);
  formulario.set('fecha', datos.fecha);
  formulario.set('referencia', datos.referencia);
  formulario.set('descripcion', datos.descripcion);
  formulario.set('observaciones', datos.observaciones);

  if (datos.comprobante) formulario.set('voucher_file', datos.comprobante);

  return formulario;
}

export const administrativosApi = {
  listar: (
    tipo: TipoAdministrativo,
    filtros: FiltrosAdministrativos,
    signal?: AbortSignal,
  ): Promise<ListadoAdministrativos> =>
    apiClient.get<ListadoAdministrativos>(
      `/api/finanzas/${tipo}${cadenaDeAdministrativos(filtros)}`,
      { signal },
    ),

  detalle: (tipo: TipoAdministrativo, id: number, signal?: AbortSignal) =>
    apiClient.get<DetalleAdministrativo>(`/api/finanzas/${tipo}/${id}`, { signal }),

  registrar: (tipo: TipoAdministrativo, datos: CuerpoAdministrativo) =>
    apiClient.post<{ id: number; message: string }>(`/api/finanzas/${tipo}`, {
      body: formularioDe(datos),
    }),

  actualizar: (tipo: TipoAdministrativo, id: number, datos: CuerpoAdministrativo) =>
    apiClient.put<{ id: number; message: string }>(`/api/finanzas/${tipo}/${id}`, {
      body: formularioDe(datos),
    }),

  anular: (tipo: TipoAdministrativo, id: number) =>
    apiClient.delete<{ id: number; message: string }>(`/api/finanzas/${tipo}/${id}`),

  urlComprobante: (tipo: TipoAdministrativo, id: number) =>
    `/api/finanzas/${tipo}/${id}/comprobante`,

  urlExportar: (tipo: TipoAdministrativo, filtros: FiltrosAdministrativos, formato: 'csv' | 'pdf') => {
    const cadena = cadenaDeAdministrativos(filtros);

    return `/api/finanzas/${tipo}/exportar${cadena}${cadena ? '&' : '?'}formato=${formato}`;
  },
};

/** Listado paginado con sus catálogos y totales. */
export function useAdministrativos(tipo: TipoAdministrativo, filtros: FiltrosAdministrativos) {
  return useQuery({
    queryKey: administrativosKeys.lista(tipo, filtros),
    queryFn: ({ signal }) => administrativosApi.listar(tipo, filtros, signal),
  });
}

/** Detalle de un movimiento (para la ficha). */
export function useDetalleAdministrativo(tipo: TipoAdministrativo, id: number | null) {
  return useQuery({
    queryKey: administrativosKeys.detalle(tipo, id ?? 0),
    queryFn: ({ signal }) => administrativosApi.detalle(tipo, id as number, signal),
    enabled: Number(id) > 0,
  });
}

export function useRegistrarAdministrativo(tipo: TipoAdministrativo) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CuerpoAdministrativo) => administrativosApi.registrar(tipo, datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: administrativosKeys.todo(tipo) }),
  });
}

export function useActualizarAdministrativo(tipo: TipoAdministrativo) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: CuerpoAdministrativo }) =>
      administrativosApi.actualizar(tipo, id, datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: administrativosKeys.todo(tipo) }),
  });
}

export function useAnularAdministrativo(tipo: TipoAdministrativo) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => administrativosApi.anular(tipo, id),
    onSuccess: () => cliente.invalidateQueries({ queryKey: administrativosKeys.todo(tipo) }),
  });
}
